/**
 * The "Extract KPIs" composer: metrics, folders, period.
 *
 * It validates the same three rules the backend does (at least one metric; a
 * folder when the scope is "selected"; a range that runs forwards) so the user
 * sees the problem beside the control rather than as a server error — but the
 * backend still enforces them, because this check is a convenience, not the gate.
 *
 * Every workflow is created with `auto_run: false`: an upload never starts an
 * extraction by itself, so there is no choice to offer. The field is still sent
 * explicitly rather than omitted, because the API schema still carries it.
 */

import { Loader2 } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import type {
	CreateWorkflowPayload,
	FolderScope,
	MetricGroup,
	PeriodInterval,
} from "../../types/kpiWorkflow";
import FolderScopePicker, { type FolderOption } from "./FolderScopePicker";
import MetricChecklist from "./MetricChecklist";
import PeriodRangeFields from "./PeriodRangeFields";
import {
	BTN,
	BTN_PRIMARY,
	CARD,
	FIELD_LABEL,
	HINT,
	INPUT,
	LINK,
	TITLE,
} from "./composerStyles";

interface WorkflowComposerProps {
	metricGroups: MetricGroup[];
	folders: FolderOption[];
	/** True while the folder tree is still in flight; see FolderScopePicker. */
	foldersLoading?: boolean;
	/** True when the folder tree failed to load; see FolderScopePicker. */
	foldersError?: boolean;
	/** Re-fires the folder fetch from the picker's Retry. */
	onRetryFolders?: () => void;
	isSaving: boolean;
	onCancel: () => void;
	onSubmit: (payload: CreateWorkflowPayload) => void;
}

const DEFAULT_NAME = "Extract KPIs";

const SectionHeading: React.FC<{ title: string; hint?: string }> = ({
	title,
	hint,
}) => (
	<div className="mb-2">
		<h3 className={TITLE}>{title}</h3>
		{hint && <p className={`mt-0.5 ${HINT}`}>{hint}</p>}
	</div>
);

const WorkflowComposer: React.FC<WorkflowComposerProps> = ({
	metricGroups,
	folders,
	foldersLoading = false,
	foldersError = false,
	onRetryFolders,
	isSaving,
	onCancel,
	onSubmit,
}) => {
	const [name, setName] = useState(DEFAULT_NAME);
	const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
		() => new Set(),
	);
	const [scope, setScope] = useState<FolderScope>("all");
	const [selectedFolders, setSelectedFolders] = useState<Set<number>>(
		() => new Set(),
	);
	const [periodStart, setPeriodStart] = useState("");
	const [periodEnd, setPeriodEnd] = useState("");
	const [interval, setInterval] = useState<PeriodInterval>("monthly");
	const [error, setError] = useState<string | null>(null);

	const allMetricKeys = useMemo(
		() => metricGroups.flatMap((group) => group.metrics.map((m) => m.key)),
		[metricGroups],
	);

	const toggleMetric = useCallback((key: string) => {
		setSelectedMetrics((current) => {
			const next = new Set(current);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	const toggleGroup = useCallback((keys: string[], nextSelected: boolean) => {
		setSelectedMetrics((current) => {
			const next = new Set(current);
			for (const key of keys) {
				if (nextSelected) {
					next.add(key);
				} else {
					next.delete(key);
				}
			}
			return next;
		});
	}, []);

	const toggleFolder = useCallback((id: number) => {
		setSelectedFolders((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const selectEveryMetric = useCallback(() => {
		setSelectedMetrics(new Set(allMetricKeys));
	}, [allMetricKeys]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (selectedMetrics.size === 0) {
			setError("Pick at least one metric to extract.");
			return;
		}
		if (scope === "selected" && selectedFolders.size === 0) {
			setError("Pick at least one folder, or switch to all folders.");
			return;
		}
		if (periodStart && periodEnd && periodStart > periodEnd) {
			setError("The end of the period range comes before its start.");
			return;
		}
		setError(null);
		onSubmit({
			name: name.trim() || DEFAULT_NAME,
			template_key: "extract_kpis",
			folder_scope: scope,
			folder_ids: scope === "selected" ? Array.from(selectedFolders) : [],
			metric_keys: allMetricKeys.filter((key) => selectedMetrics.has(key)),
			period_start: periodStart || null,
			period_end: periodEnd || null,
			interval,
			auto_run: false,
		});
	};

	return (
		<form
			onSubmit={handleSubmit}
			className={`${CARD} space-y-6 p-5`}
		>
			<div>
				<label
					htmlFor="kpi-workflow-name"
					className={`mb-1 block ${FIELD_LABEL}`}
				>
					Workflow name
				</label>
				<input
					id="kpi-workflow-name"
					type="text"
					className={INPUT}
					value={name}
					onChange={(event) => setName(event.target.value)}
					maxLength={255}
				/>
			</div>

			<section>
				<div className="flex items-start justify-between">
					<SectionHeading
						title="Metrics"
						hint="Grouped the way the metric pack is grouped."
					/>
					<button
						type="button"
						onClick={selectEveryMetric}
						className={LINK}
					>
						Select everything
					</button>
				</div>
				<MetricChecklist
					groups={metricGroups}
					selected={selectedMetrics}
					onToggleMetric={toggleMetric}
					onToggleGroup={toggleGroup}
				/>
			</section>

			<section>
				<SectionHeading title="Folders" />
				<FolderScopePicker
					folders={folders}
					isLoading={foldersLoading}
					hasError={foldersError}
					onRetry={onRetryFolders}
					scope={scope}
					selectedIds={selectedFolders}
					onScopeChange={setScope}
					onToggleFolder={toggleFolder}
				/>
			</section>

			<section>
				<SectionHeading
					title="Period"
					hint="Leave a date empty to cover everything on that side."
				/>
				<PeriodRangeFields
					periodStart={periodStart}
					periodEnd={periodEnd}
					interval={interval}
					onPeriodStartChange={setPeriodStart}
					onPeriodEndChange={setPeriodEnd}
					onIntervalChange={setInterval}
				/>
			</section>

			{error && (
				<p role="alert" className="text-sm text-red-600">
					{error}
				</p>
			)}

			{/*
			 * The submit row sticks to the bottom of the composer.
			 *
			 * The metric pack is 124 checkboxes, so "Create workflow" sat below
			 * all of them: a user had to scroll the length of the catalogue to
			 * reach it, and after ticking four metrics near the top had no
			 * visible way to commit. Keeping the way OUT of the composer visible
			 * is why the open/closed state moved into a `?compose=` search param;
			 * this is the same reasoning applied to the way forward.
			 */}
			<div className="sticky bottom-0 -mx-4 flex items-center gap-2 border-t border-gray-200 bg-white px-4 py-3">
				<button
					type="submit"
					disabled={isSaving}
					className={BTN_PRIMARY}
				>
					{isSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
					Create workflow
				</button>
				<button
					type="button"
					onClick={onCancel}
					className={BTN}
				>
					Cancel
				</button>
			</div>
		</form>
	);
};

export default WorkflowComposer;
